import {
  EnvelopeLight,
  Hyperlink3Bold,
  MessagingWhatsappBold,
  SocialMediaFacebook1Light,
  SocialMediaTwitterBold,
} from '@ha/icons';
import React from 'react';
import { useLocation } from 'react-router-dom';
import CopyToClipboard from 'react-copy-to-clipboard';
import cx from 'classnames';
import { useIntl } from 'ha/i18n';
import { ShareToSocialNetworks } from 'ha/components.legacy/ShareToSocialNetworks';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';
import { Link } from 'ha/ui.legacy/Link';
import { Button } from 'ha/components.legacy/Button';

const commonClasses = 'btn blog-social-button p-x-0';

const btnKinds = {
  copy: {
    default: 'brand',
    defaultGrey: 'grey-light',
    sidebar: 'orange-outline',
  },
  email: {
    default: 'success',
    sidebar: 'mint-outline',
    defaultGrey: 'grey-light',
  },
  facebook: {
    default: 'facebook',
    sidebar: 'facebook-outline',
    defaultGrey: 'grey-light',
  },
  twitter: {
    default: 'twitter',
    sidebar: 'twitter-outline',
    defaultGrey: 'grey-light',
  },
  whatsapp: {
    default: 'whatsapp',
    sidebar: 'whatsapp-outline',
    defaultGrey: 'grey-light',
  },
};

interface OwnProps {
  isMobile: boolean;
  kind: 'default' | 'defaultGrey' | 'full' | 'sidebar';
  title: string;
}

type Props = OwnProps;

export const BlogShareToSocialNetworks: React.FC<Props> = ({
  isMobile,
  kind = 'default',
  title,
}) => {
  const { T, urlResolver } = useIntl();
  const location = useLocation();

  return (
    <ShareToSocialNetworks location={location} shareMessage={title}>
      {({ shareUrls }) => (
        <div
          className={cx('flexbox', {
            'flex-direction__column': kind === 'full',
          })}
        >
          <Track name="Clicked a copy" type="copy">
            <CopyToClipboard
              text={urlResolver.absolutizePath(location.pathname, {
                skipAddLanguage: true,
              })}
            >
              <Button
                kind={btnKinds.copy[kind] || btnKinds.copy.default}
                size={kind === 'full' ? 'lg' : undefined}
                title={T('Copy link')}
                className={cx({
                  [commonClasses]: kind !== 'full',
                  'm-b-1_5 blog-social-button--full': kind === 'full',
                })}
              >
                {kind === 'full' && T('Copy link')}
                <Hyperlink3Bold
                  aria-hidden="true"
                  className={cx('p-y-0', { 'm-l-0_7': kind === 'full' })}
                  fontSize="small"
                />
              </Button>
            </CopyToClipboard>
          </Track>
          <Track name="Clicked a share button" type="email">
            <Link
              to={shareUrls.email}
              target="_self"
              className={cx(
                commonClasses,
                kind === 'full'
                  ? 'm-b-1_5 btn-lg blog-social-button--full'
                  : 'm-l-0_7',
                `btn-${btnKinds.email[kind] || btnKinds.email.default}`,
              )}
              title={T('Share via E-Mail')}
              rel="noopener noreferer nofollow"
            >
              {kind === 'full' && T('Email to someone')}
              <EnvelopeLight
                aria-hidden="true"
                className={cx('m-t-0_3', { 'm-l-0_7': kind === 'full' })}
                fontSize="small"
              />
            </Link>
          </Track>
          <Track name="Clicked a share button" type="facebook">
            <Link
              to={shareUrls.facebook}
              target="_blank"
              className={cx(
                commonClasses,
                kind === 'full'
                  ? 'm-b-1_5 btn-lg blog-social-button--full'
                  : 'm-l-0_7',
                `btn-${btnKinds.facebook[kind] || btnKinds.facebook.default}`,
                'blog-social-button--facebook',
              )}
              title={T('Share via Facebook')}
              rel="noopener noreferer nofollow"
            >
              {kind === 'full' && T('Share on Facebook')}
              <SocialMediaFacebook1Light
                aria-hidden="true"
                className={cx('m-t-0_3', { 'm-l-0_7': kind === 'full' })}
                fontSize="small"
              />
            </Link>
          </Track>
          <Track name="Clicked a share button" type="twitter">
            <Link
              to={shareUrls.twitter}
              target="_blank"
              rel="noopener noreferer nofollow"
              className={cx(
                commonClasses,
                kind === 'full'
                  ? 'm-b-1_5 btn-lg blog-social-button--full'
                  : 'm-l-0_7',
                `btn-${btnKinds.twitter[kind] || btnKinds.twitter.default}`,
                'blog-social-button--twitter',
              )}
              title={T('Share via Twitter')}
            >
              {kind === 'full' && T('Share on Twitter')}
              <SocialMediaTwitterBold
                aria-hidden="true"
                className={cx('m-t-0_3', { 'm-l-0_7': kind === 'full' })}
                fontSize="small"
              />
            </Link>
          </Track>
          {isMobile && (
            <Track name="Clicked a share button" type="whatsapp">
              <Link
                to={shareUrls.whatsapp}
                className={cx(
                  commonClasses,
                  kind === 'full'
                    ? 'btn-lg blog-social-button--full'
                    : 'm-l-0_7 blog-social-button--whatsapp',
                  `btn-${btnKinds.whatsapp[kind] || btnKinds.whatsapp.default}`,
                )}
                data-action="share/whatsapp/share"
                title={T('Share via Whatsapp')}
                rel="noopener noreferer nofollow"
              >
                {kind === 'full' && T('Share on Whatsapp')}
                <MessagingWhatsappBold
                  aria-hidden="true"
                  className={cx('m-t-0_3', { 'm-l-0_7': kind === 'full' })}
                  fontSize="small"
                />
              </Link>
            </Track>
          )}
        </div>
      )}
    </ShareToSocialNetworks>
  );
};
